/**
 * The console's connection to the dispatcher hub.
 *
 * One connection per console, shared by every panel that needs it. Opening a socket per
 * component would multiply connections by however many panels happen to be mounted, and
 * the server counts those against a real limit.
 */

import { HubConnectionBuilder, HubConnectionState, LogLevel, type HubConnection } from '@microsoft/signalr';
import { useEffect, useRef, useSyncExternalStore } from 'react';

import { freshAccessToken } from '@taxi/api-client';

export interface DispatcherHubHandlers {
  onOrderChanged?: () => void;
  onDriverMoved?: (movement: DriverMoved) => void;
  onDriverShiftChanged?: () => void;

  /** The telephone is ringing, and here is who it is. */
  onCallRinging?: (call: IncomingCall) => void;

  /** Somebody took it, so it clears from this screen. */
  onCallTaken?: (taken: CallTaken) => void;

  /** It is over. */
  onCallFinished?: (finished: CallFinished) => void;

  /** A missed call joined the queue, or somebody took it. */
  onMissedCallChanged?: (change: MissedCallChanged) => void;

  /** A caller was parked for a car, or stopped waiting. */
  onCallHeldChanged?: (change: CallHeldChanged) => void;

  /** A caller went on hold on the PBX, or came off it. */
  onCallParkedChanged?: (change: CallParkedChanged) => void;

  /** Somebody else sat down at the desk this dispatcher had. Sent to this dispatcher only. */
  onDeskTakenOver?: (notice: DeskTakenOver) => void;

  /** A driver wrote to the office, the office wrote to a driver, or either read the other. */
  onDriverMessageChanged?: (change: DriverMessageChanged) => void;

  /** A driver raised the silent alarm, or somebody cleared it. */
  onDriverAlarmChanged?: (change: DriverAlarmChanged) => void;

  /**
   * A phone asked to be a service device, or the office answered one. Nothing in it: the list
   * is an administrator's, so the console asks for it and a dispatcher is refused.
   */
  onServiceDeviceRequestsChanged?: () => void;

  /** Called after the socket comes back, when whatever was missed has to be refetched. */
  onReconnected?: () => void;
}

/** One of the caller's saved addresses, as the pop offers it. */
export interface CallAddress {
  addressId: string;
  label: string | null;
  address: string;
  longitude: number;
  latitude: number;
  notes: string | null;
}

/**
 * A call arriving, with everything already known about who it is.
 *
 * The customer and their addresses travel with the event rather than being fetched after
 * it. The window between the first ring and somebody saying hello is about four seconds,
 * and a round trip in the middle of it is a form that fills in after the conversation
 * started.
 */
export interface IncomingCall {
  callId: string;
  from: string | null;
  to: string | null;
  line: string | null;
  customerId: string | null;
  customerName: string | null;
  blocked: boolean;
  notes: string | null;
  addresses: CallAddress[];
  at: string;
}

/** One line of a driver's thread, as every desk hears of it. */
export interface DriverMessageChanged {
  messageId: string;
  driverId: string;
  driverName: string;
  direction: 'ToDriver' | 'FromDriver';
  code: string | null;
  body: string | null;
  sentByUserId: string | null;
  sentAt: string;
  readAt: string | null;
}

/** An alarm raised or cleared, with everything the desk needs to act. */
export interface DriverAlarmChanged {
  alarmId: string;
  driverId: string;
  driverName: string;
  callSign: string | null;
  vehiclePlate: string | null;
  phoneNumber: string | null;
  longitude: number | null;
  latitude: number | null;
  orderId: string | null;
  orderReference: string | null;
  raisedAt: string;
  clearedAt: string | null;
}

export interface CallTaken {
  callId: string;
  extension: string | null;
  userId: string | null;
  at: string;
}

export interface CallFinished {
  callId: string;
  outcome: string;
  needsCallingBack: boolean;
  at: string;
}

export interface MissedCallChanged {
  callId: string;
  inQueue: boolean;
  handledByUserId: string | null;
  handledByName: string | null;
  at: string;
}

/**
 * A caller was parked for a car, or stopped waiting.
 *
 * Carries the counts as well as the change, so the panel's header does not have to ask
 * the server how full the lines are after every message.
 */
export interface CallHeldChanged {
  callId: string;
  orderId: string | null;
  held: boolean;
  heldCount: number;
  maxHeld: number;
  at: string;
}

/** A caller went on hold on the PBX, or came off it. The list is refetched, not patched. */
export interface CallParkedChanged {
  callId: string;
  parked: boolean;
  parkedByUserId: string | null;
  at: string;
}

/** Somebody sat down at the desk this dispatcher had; its phones now ring for them. */
export interface DeskTakenOver {
  deskId: string;
  deskName: string;
  byUserId: string;
  byName: string | null;
  at: string;
}

export interface DriverMoved {
  driverId: string;
  longitude: number;
  latitude: number;
  headingDegrees: number | null;
  reportedAt: string;
}

let shared: HubConnection | null = null;
let refCount = 0;

/**
 * The wait before each attempt to connect again; the last repeats for as long as it takes.
 *
 * Backoff rather than a fixed interval, because a console reconnecting every second through
 * a deploy turns a brief restart into a stampede. And no end to it: the stock policy gave up
 * after five tries, under a minute, so an API that took longer than that to come back left
 * every console showing a map that had stopped — permanently, and with nothing to say so.
 */
const BACKOFF_MS = [0, 2000, 5000, 10000, 30000];

function backoff(attempt: number): number {
  return BACKOFF_MS[Math.min(attempt, BACKOFF_MS.length - 1)];
}

/** The next try at a first connection that failed, while one is waiting. */
let retryTimer: ReturnType<typeof setTimeout> | undefined;

/**
 * Whether live updates are arriving, for the bar to say when they are not.
 *
 * True until the connection is lost rather than until it is first made, so the bar does not
 * flash a warning at every sign-in while the socket is being opened.
 */
let live = true;
let goingDown: ReturnType<typeof setTimeout> | undefined;
const liveListeners = new Set<() => void>();

/**
 * How long a drop may last before it is announced. The first reconnect is immediate, and a
 * socket that comes straight back is nothing a dispatcher needs to read about.
 */
const GRACE_MS = 3000;

function announce(up: boolean): void {
  if (up) {
    clearTimeout(goingDown);
    goingDown = undefined;
    setLive(true);
  } else if (live && goingDown === undefined) {
    goingDown = setTimeout(() => {
      goingDown = undefined;
      setLive(false);
    }, GRACE_MS);
  }
}

function setLive(next: boolean): void {
  if (live === next) return;

  live = next;
  for (const listener of liveListeners) listener();
}

/** False while the console has lost its live connection and is trying to get it back. */
export function useHubLive(): boolean {
  return useSyncExternalStore(
    (listener) => {
      liveListeners.add(listener);
      return () => liveListeners.delete(listener);
    },
    () => live,
  );
}

/**
 * Everyone who wants telling when the socket comes back.
 *
 * A set rather than one callback per hook instance, because `onreconnected` has no
 * counterpart that removes a handler: registering inside each hook's effect would leave
 * a dead closure behind on every remount, and every consumer that ever mounted would be
 * called on the next reconnect. Registering once per connection and iterating this set
 * keeps the count equal to the number of panels actually on screen.
 */
const resyncListeners = new Set<() => void>();

/**
 * Waits until the connection is actually in its group.
 *
 * `start()` resolves at the handshake, but the server puts the connection into its
 * tenant group in `OnConnectedAsync`, after two more round trips — so there is a window
 * in which the console believes it is connected and an event published right then
 * reaches nobody: not late, dropped. The server does not dispatch `Ready()` until the
 * join is done, so awaiting it closes the window. See #127.
 */
async function subscribed(hub: HubConnection): Promise<void> {
  try {
    await hub.invoke('Ready');
  } catch {
    // A server from before Ready() existed — possible mid-deploy. The window stays
    // open, which is what every build before this one lived with.
  }
}

function connection(): HubConnection {
  if (shared !== null) return shared;

  shared = new HubConnectionBuilder()
    .withUrl('/hubs/dispatcher', {
      // Asked for on every connect, and refreshed first when it is about to lapse. An
      // access token lives about fifteen minutes and the socket can outlive many of them;
      // the one in memory when it drops may be long dead, the refusal is not retried with
      // another, and handing it over again is how the map used to stop for good. A session
      // the server will not refresh ends through the client, and the sign-in screen says so.
      accessTokenFactory: async () => (await freshAccessToken()) ?? '',
    })
    .withAutomaticReconnect({
      nextRetryDelayInMilliseconds: ({ previousRetryCount }) => backoff(previousRetryCount),
    })
    .configureLogging(LogLevel.Warning)
    .build();

  const hub = shared;

  hub.onreconnecting(() => announce(false));

  // A reconnect means events were missed while the socket was down, so every panel
  // refetches rather than trusting the deltas it has — but only after the group join,
  // or an event landing between the refetch and the join is lost the same way.
  hub.onreconnected(() => {
    announce(true);
    void subscribed(hub).then(() => {
      for (const listener of resyncListeners) listener();
    });
  });

  // Closed with no reconnect coming — the server ended it outright. Unless the last panel
  // stopped it, which leaves `shared` pointing elsewhere, start again from the beginning.
  hub.onclose(() => {
    if (shared !== hub) return;

    announce(false);
    begin(hub, 0);
  });

  return shared;
}

/**
 * Connects, and keeps trying until it has.
 *
 * The automatic reconnect only covers a connection that was once made. A console opened
 * while the API is restarting fails its first `start()`, and that used to be the end of it.
 */
function begin(hub: HubConnection, attempt: number): void {
  hub.start().then(
    async () => {
      announce(true);

      // Panels fetched on mount, while the connection was still joining its group, so an
      // event from those moments is on the server and not on any screen. One refetch once
      // deltas hold makes the first picture trustworthy; it costs each panel a request it
      // was about to be able to trust anyway.
      await subscribed(hub);
      for (const listener of resyncListeners) listener();
    },
    () => {
      announce(false);

      retryTimer = setTimeout(() => {
        retryTimer = undefined;

        if (shared === hub && hub.state === HubConnectionState.Disconnected) {
          begin(hub, attempt + 1);
        }
      }, backoff(attempt + 1));
    },
  );
}

export function useDispatcherHub(handlers: DispatcherHubHandlers): void {
  // Held in a ref so re-rendering with new closures does not detach and reattach every
  // handler, which would drop events in the gap. Written in an effect rather than
  // during render: React may render a component more than once before committing, and
  // mutating a ref on a render that is thrown away leaves the handler pointing at a
  // closure that never ran.
  const latest = useRef(handlers);

  useEffect(() => {
    latest.current = handlers;
  });

  useEffect(() => {
    const hub = connection();
    refCount += 1;

    const onOrderChanged = () => latest.current.onOrderChanged?.();
    const onDriverMoved = (movement: DriverMoved) => latest.current.onDriverMoved?.(movement);
    const onDriverShiftChanged = () => latest.current.onDriverShiftChanged?.();
    const onCallRinging = (call: IncomingCall) => latest.current.onCallRinging?.(call);
    const onCallTaken = (taken: CallTaken) => latest.current.onCallTaken?.(taken);
    const onCallFinished = (finished: CallFinished) => latest.current.onCallFinished?.(finished);
    const onMissedCallChanged = (change: MissedCallChanged) =>
      latest.current.onMissedCallChanged?.(change);
    const onCallHeldChanged = (change: CallHeldChanged) =>
      latest.current.onCallHeldChanged?.(change);
    const onCallParkedChanged = (change: CallParkedChanged) =>
      latest.current.onCallParkedChanged?.(change);
    const onDeskTakenOver = (notice: DeskTakenOver) => latest.current.onDeskTakenOver?.(notice);
    const onDriverMessageChanged = (change: DriverMessageChanged) =>
      latest.current.onDriverMessageChanged?.(change);
    const onDriverAlarmChanged = (change: DriverAlarmChanged) =>
      latest.current.onDriverAlarmChanged?.(change);
    const onServiceDeviceRequestsChanged = () => latest.current.onServiceDeviceRequestsChanged?.();

    hub.on('orderChanged', onOrderChanged);
    hub.on('driverMoved', onDriverMoved);
    hub.on('driverShiftChanged', onDriverShiftChanged);
    hub.on('callRinging', onCallRinging);
    hub.on('callTaken', onCallTaken);
    hub.on('callFinished', onCallFinished);
    hub.on('missedCallChanged', onMissedCallChanged);
    hub.on('callHeldChanged', onCallHeldChanged);
    hub.on('callParkedChanged', onCallParkedChanged);
    hub.on('deskTakenOver', onDeskTakenOver);
    hub.on('driverMessageChanged', onDriverMessageChanged);
    hub.on('driverAlarmChanged', onDriverAlarmChanged);
    hub.on('serviceDeviceRequestsChanged', onServiceDeviceRequestsChanged);

    const resync = () => latest.current.onReconnected?.();
    resyncListeners.add(resync);

    // Not while a retry is already waiting: a second panel mounting in that gap would
    // otherwise start a second chain of attempts beside the first.
    if (hub.state === HubConnectionState.Disconnected && retryTimer === undefined) {
      begin(hub, 0);
    }

    return () => {
      hub.off('orderChanged', onOrderChanged);
      hub.off('driverMoved', onDriverMoved);
      hub.off('driverShiftChanged', onDriverShiftChanged);
      hub.off('callRinging', onCallRinging);
      hub.off('callTaken', onCallTaken);
      hub.off('callFinished', onCallFinished);
      hub.off('missedCallChanged', onMissedCallChanged);
      hub.off('callHeldChanged', onCallHeldChanged);
      hub.off('callParkedChanged', onCallParkedChanged);
      hub.off('deskTakenOver', onDeskTakenOver);
      hub.off('driverMessageChanged', onDriverMessageChanged);
      hub.off('driverAlarmChanged', onDriverAlarmChanged);
      hub.off('serviceDeviceRequestsChanged', onServiceDeviceRequestsChanged);
      resyncListeners.delete(resync);

      refCount -= 1;

      if (refCount === 0) {
        clearTimeout(retryTimer);
        retryTimer = undefined;

        // Let go before stopping: stop() can call onclose on the spot, and onclose restarts
        // any connection that is still the shared one.
        const closing = shared;
        shared = null;
        void closing?.stop();

        // Nobody is listening any more — signed out, most often — so there is nothing to
        // be missing. The next console starts from a clean bar.
        announce(true);
      }
    };
  }, []);
}
